import React from "react"
import styled, {ThemeProvider} from "styled-components"

import LogoSVG from "../../static/svg/logo.svg";

import FacebookSVG from "../../static/svg/icons/facebook.svg";
import InstagramSVG from "../../static/svg/icons/instagram.svg";

import { useSocialMedia } from "../data/hooks/social-media";
import {
  Heading1,
  Heading2, Heading3,
  InternalLink, MaxWidthContainer,
  MiniParagraph,
  Paragraph
} from "../style/itf-styled-components";
import {useFooterLinks} from "../data/hooks/footer-links";
import {useThemes} from "../style/themes.hook";
import {ExternalLink} from "./external-link";

const Container = styled.footer`
  color: ${props => props.theme.colors.neutrals._900};
  background-color: ${props => props.theme.colors.neutrals._000};
  font-size: 0.8em;
  display: flex;
  flex-direction: column;
  width: 100%;
  align-items: center;
`;

const Bottom = styled.div`
  display: grid;
  grid-template-columns: repeat(120, 1fr);
  padding: 50px 25px;
  
  @media (max-width: 768px) {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 0;
  }
`;
const BottomLeft = styled.div`
  grid-column: 1/31;
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    align-items: center;
    width: 100%;
    border-bottom: 1px solid #C8CDD0;
    padding: 25px;
  }
`;
const BottomRight = styled.div`
  grid-column: 31/121;
  display: flex;
  justify-content: space-around;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    width: 100%;
    padding: 25px;
  }
`;

const LinkSet = styled.div`
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    flex-direction: column;
    align-items: center;
    width: 100%;
  }
`;
const FooterLogo = styled(LogoSVG)`
  height: 80px;
  align-self: start;
  
  path.symbol {
    fill: ${props => props.theme.colors.primary._500};
  }
  path.text {
    fill: ${props => props.theme.colors.neutrals._900};
  }
  
  @media (max-width: 768px) {
    align-self: center;
  }
`;

const SocialMediaItems = styled.div`
  display: flex;
`;

const SocialMediaLink = styled(ExternalLink)`
  margin-right: 15px;
  
  :last-child {
    margin-right: 0;
  }
`;

const SocialMediaIcon = styled.span`
  width: 20px;
  height: 20px;

  path {
    fill: ${props => props.theme.colors.neutrals._900};
  }
  
  :hover {
    path {
      fill: ${props => props.theme.colors.primary._500};
    }
  }
`;
const FooterLink = styled(InternalLink)`
  font-size: 1em;
  padding: 5px 0;
  text-decoration: underline;
  font-weight: 700;
  color: ${props => props.theme.colors.neutrals._900};
  
  :visited {
    color: ${props => props.theme.colors.neutrals._900};
  }
  
  :hover {
    color: ${props => props.theme.colors.primary._500};
  }
  
  @media (max-width: 768px) {
    padding: 10px 0;
  }
`;

const Contact = styled.div`
  display: flex;
  flex-direction: column;
  
  @media (max-width: 768px) {
    margin-top: 50px;
    align-items: center;
  }
`;

const ContactHeading = styled(Heading2)`
  padding: 0 0 5px 0;
`;

const ContactOrganizer = styled(Heading3)`
  color: ${props => props.theme.colors.primary.base};
  padding: 5px 0;
`;

const Footer = ({ className }) => {
  const socialMediaItems = useSocialMedia() || [];
  const footerLinks = useFooterLinks() || [];
  const themes = useThemes();

  return (
    <ThemeProvider theme={themes.contrastLight}>
      <Container className={className}>
        <MaxWidthContainer>
          <Bottom>
            <BottomLeft>
              <FooterLogo />
              <Heading1 css={`
                font-size: 2em;
              `}>Focus to inspire!</Heading1>

              <Paragraph>
                &#169; Copyright 2021, Asociația Imagine Timișoara.
                Toate drepturile rezervate
              </Paragraph>
            </BottomLeft>

            <BottomRight>
              {footerLinks.map((set, index) => (
                <LinkSet key={index}>
                  {(set.links || []).map((link, index) => (
                    <FooterLink
                      as={link.isExternal && ExternalLink}
                      to={link.to}
                      key={index}
                    >{link.label}</FooterLink>
                  ))}
                </LinkSet>
              ))}

              <div>
                <div css={`
                  margin-bottom: 30px;
                  display: flex;
                  flex-direction: column;
                  
                  @media (max-width: 768px) {
                    margin-top: 50px;
                    align-items: center;
                  }
                `}
                >
                  <Heading2
                    as="h2"
                    css={`
                    padding-top: 0;
                  `}
                  >Social</Heading2>
                  <SocialMediaItems>
                    {socialMediaItems.map((item, index) => (
                      <SocialMediaLink to={item.link} key={index}>
                        <SocialMediaIcon as={{
                          'facebook': () => FacebookSVG,
                          'instagram': () => InstagramSVG
                        }[item.slug]()}
                        />
                      </SocialMediaLink>
                    ))}
                  </SocialMediaItems>
                </div>
                <Contact>
                  <ContactHeading>Contact</ContactHeading>
                  <ContactOrganizer>Asociația Imagine Timisoara</ContactOrganizer>
                  <MiniParagraph>0735 392 303</MiniParagraph>
                  <MiniParagraph>office@imaginetimisoara.ro</MiniParagraph>
                  <MiniParagraph>Timișoara, str. Pestalozzi nr. 22, et. 1, cam. 3</MiniParagraph>
                  <MiniParagraph>CIF 41031713</MiniParagraph>
                </Contact>
              </div>
            </BottomRight>
          </Bottom>
        </MaxWidthContainer>
      </Container>
    </ThemeProvider>
  );
};

export default Footer;
